import * as React from 'react'
import cx from 'classnames'
import { useEffect, useRef, Fragment } from 'react'

import { Icon, Image, Button, Loader } from '@metrisk/react_metrisk-components'
import UploaderMimeTypeIcon from './UploaderMimeTypeIcon'
import { humanReadableBytes } from '@utils/file'

interface UploaderFileProps {
  file: Upload.DirectorFile
  children?: any
  placeholder?: any
  changeFileText?: string
  addingFiles?: boolean
  uploading?: boolean
  fit?: boolean
  hasError: boolean
  onClick: () => void
  onRemove: () => void
}

const UploaderFile = ({
  file,
  onClick,
  onRemove,
  children,
  hasError,
  placeholder = null,
  changeFileText = 'Change File',
  addingFiles = false,
  uploading = false,
  fit = false,
}: UploaderFileProps) => {
  const preview = useRef(null)

  const isImage = () => {
    return file && file.type.substring(0, 5) === 'image'
  }

  const getSize = () => {
    return file ? humanReadableBytes(file.data.size, true) : 'unknown size'
  }

  const handlePreviewClick = () => {
    !file && onClick()
  }

  useEffect(() => {
    if (file && isImage()) {
      preview.current = URL.createObjectURL(file.data)
    }

    return () => {
      URL.revokeObjectURL(preview.current)
    }
  }, [file])

  const renderPreview = () => {
    if (addingFiles) {
      return <Loader type={'Bounce'} />
    }

    return isImage() ? (
      <Image className="uploader-file__preview-image" src={preview.current} />
    ) : (
      <UploaderMimeTypeIcon mimeType={file?.type} filename={file?.name} placeholder={placeholder} />
    )
  }

  return (
    <div className={`uploader-file ${fit ? 'uploader-file--fit' : ''}`}>
      <div
        className={`uploader-file__preview ${file ? 'uploader-file__preview--active' : ''} ${
          fit ? 'uploader-file__preview--fit' : ''
        }`}
        onClick={handlePreviewClick}
      >
        {renderPreview()}
      </div>
      <div className={`uploader-file__info-wrapper ${fit ? 'uploader-file__info-wrapper--fit' : ''}`}>
        <div className={`uploader-file__info ${file ? 'uploader-file__info--active' : ''}`}>
          <div className="uploader-file__info-column">
            {file && <div className="uploader-file__name">{file.name}</div>}
            {file && (
              <div className="uploader-file__details">
                <span>{file?.type}</span>
                <span>{getSize()}</span>
              </div>
            )}
          </div>
        </div>
        <div className="uploader-file__buttons">
          {file && (
            <Fragment>
              <Button
                className={cx({ 'btn--full': fit })}
                type={'Secondary'}
                onClick={onClick}
                disabled={hasError || uploading || addingFiles}
              >
                {changeFileText}
              </Button>
              <Button
                type={'Secondary'}
                className={cx('uploader-file__remove')}
                onClick={onRemove}
                disabled={hasError || uploading || addingFiles}
              >
                <Icon name="cross" colour={'Light'} size="Large" />
              </Button>
            </Fragment>
          )}
        </div>
        {children}
      </div>
    </div>
  )
}

export default UploaderFile
